import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState, useMemo } from 'react'
import { useAlert } from 'react-alert'
import { secToTwoDigitsHourMinute, digitsHourMinuteToNumberHourMinute } from '../../../../utils/date'
import { useDispatch } from 'react-redux'
import { createMembersWeeklyTrackTime, updateMembersWeeklyTrackTime } from '../../../../states/containers/setting/memberWeeklyTrackTimeSlice'
import { updateCompanyWeeklyTrackTime } from '../../../../states/containers/setting/companyTrackTimeSlice'
import { companyWeeklyTrackTime, patchMemberWeeklyTrackTimeRequest, postMemberWeeklyTrackTimeRequest } from '../../../../states/types/setting'
import AddMemberModule from '../../setting/team/AddMemberModule'

interface editModalState {
  display: boolean
  type: string
  editData: weekly_track_time[] | null
  targetMemberData: membersModel[] | null
  selectableMemberData: SelectableMemberData[] | null
}

interface SelectableMemberData {
  id: number
  name: string | null
  kana: string | null
  email: string | null
  code: string | null
  contract: string | null
  profile_image: string | null
}

interface membersModel {
  id: number
  name: string | null
  kana: string | null
  email: string | null
  code: string | null
  contract: string | null
  profile_image: string | null
  change_authority: boolean
  weekly_track_times: weekly_track_time[]
}

interface weekly_track_time {
  week_number: number
  track_start_time: string | null
  track_stop_time: string | null
}

interface props {
  editModalState: editModalState
  closeCallback: VoidFunction
}

const EditModal: FC<props> = (props) => {
  const dispatch = useDispatch()
  const alert = useAlert()

  const { editModalState, closeCallback } = props
  const [weeklyTrackTime, setWeeklyTrackTime] = useState<weekly_track_time[]>([
    {
      week_number: 0,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 1,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 2,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 3,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 4,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 5,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
    {
      week_number: 6,
      track_start_time: "00:00",
      track_stop_time: "24:00",
    },
  ])

  const [targetMember, setTargetMember] = useState<SelectableMemberData[]>([])
  const [memberList, setMemberList] = useState<SelectableMemberData[]>([])
  const [limitTrackStartWeekNumbers, setLimitTrackStartWeekNumbers] = useState<number[]>([])
  const [limitTrackStopWeekNumbers, setLimitTrackStopWeekNumbers] = useState<number[]>([])
  const [noTrackWeekNumbers, setNoTrackWeekNumbers] = useState<number[]>([])

  const [selectTypeList, setSelectTypeList] = useState<string[]>(["all", "all", "all", "all", "all", "all", "all"])

  const weekDayDisplay = (num: number): string => {
    const weekArray = ["日", "月", "火", "水", "木", "金", "土"]
    return `${weekArray[num]}曜日`
  }

  const weekDayColor = (num: number): string => {
    if (num === 0) {
      return "#FF0000"
    }
    if (num === 6) {
      return "#317AB7"
    }
    return "#000000"
  }

  useEffect(() => {
    if (editModalState.display) {
      if (editModalState.editData) {
        editModalState.editData.forEach((v) => {
          if (v.track_start_time && v.track_stop_time && v.track_start_time === v.track_stop_time) {
            noTrackWeekNumbers.push(v.week_number)
          } else {
            if (v.track_start_time) {
              limitTrackStartWeekNumbers.push(v.week_number)
            }
            if (v.track_stop_time) {
              limitTrackStopWeekNumbers.push(v.week_number)
            }
          }
        })
        const tmp = editModalState.editData.map((v) => (
          limitTrackStartWeekNumbers.includes(v.week_number) || limitTrackStopWeekNumbers.includes(v.week_number) ? {
            week_number: v.week_number,
            track_start_time: v.track_start_time || "00:00",
            track_stop_time: v.track_stop_time || "24:00",
          } : {
            week_number: v.week_number,
            track_start_time: "00:00",
            track_stop_time: "24:00",
          }
        ))
        setWeeklyTrackTime(tmp)
        setTargetMember(editModalState.targetMemberData?.map((member) => (
          {
            id: member.id,
            name: member.name,
            kana: member.kana,
            email: member.email,
            code: member.code,
            contract: member.contract,
            profile_image: member.profile_image,
          }
        )) || [])
        setMemberList(editModalState.selectableMemberData || [])
        setSelectTypeList(tmp.map((v, i) => {
          if (noTrackWeekNumbers.includes(i)) {
            return "none"
          }
          if ((v.track_start_time !== "00:00") && (v.track_stop_time === "24:00")) {
            return "start"
          }
          if ((v.track_start_time === "00:00") && (v.track_stop_time !== "24:00")) {
            return "stop"
          }
          if ((v.track_start_time !== "00:00") && (v.track_stop_time !== "24:00")) {
            return "custom"
          }
          return "all"
        }))
      }
    }
  }, [editModalState.display])

  const hourList = (selectType: string, weekNumber: number) => {
    if (selectType === "start") {
      const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_stop_time || "24:00")
      const tmpHourCount = stopTimeObj.hour > 24 ? 24 : stopTimeObj.hour
      return (
        Array((tmpHourCount) + 1).fill(null).map((_v, i) => (
          <option value={i} key={`track_start_hour ${i + 0}`}>{i}</option>
        ))
      )
    }
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_start_time || "00:00")
    return (
      Array(24 - startTimeObj.hour + 1).fill(null).map((_v, i) => (
        <option value={i + startTimeObj.hour} key={`track_end_hour ${i + startTimeObj.hour}`}>{i + startTimeObj.hour}</option>
      ))
    )
  }

  const minuteList = (selectType: string, weekNumber: number) => {
    const splitMinuteNumber = 5
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_start_time || "00:00")
    const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_stop_time || "24:00")
    if (selectType === "start") {
      if (startTimeObj.hour === stopTimeObj.hour) {
        const minuteCount = Math.floor(Number(stopTimeObj.minute / splitMinuteNumber))
        return (
          Array(minuteCount + 1).fill(null).map((_v, i) => (
            <option value={i * splitMinuteNumber} key={`track_start_minute ${i * splitMinuteNumber}`}>
              {`00${i * splitMinuteNumber}`.slice(-2)}
            </option>
          ))
        )
      }
      return (
        Array(60 / splitMinuteNumber).fill(null).map((_v, i) => (
          <option value={i * splitMinuteNumber} key={`track_start_minute ${i * splitMinuteNumber + 1}`}>
            {`00${i * splitMinuteNumber}`.slice(-2)}
          </option>
        ))
      )
    }
    if (stopTimeObj.hour > 23) {
      return (
        <option value={0}>00</option>
      )
    }
    if (startTimeObj.hour === stopTimeObj.hour) {
      const startMinuteCount = startTimeObj.minute
      return (
        Array((60 - startMinuteCount) / splitMinuteNumber).fill(null).map((_v, i) => (
          <option value={i * splitMinuteNumber + startMinuteCount} key={`track_end_minute ${i * splitMinuteNumber + startMinuteCount}`}>
            {`00${i * splitMinuteNumber + startMinuteCount}`.slice(-2)}
          </option>
        ))
      )
    }

    return (
      Array(60 / splitMinuteNumber).fill(null).map((_v, i) => (
        <option value={i * splitMinuteNumber} key={`track_end_minute ${i * splitMinuteNumber + 1}`}>
          {`00${i * splitMinuteNumber}`.slice(-2)}
        </option>
      ))
    )
  }

  const startHoursList = useMemo(() => Array(7).fill(null).map((_v, i) => hourList("start", i)), [weeklyTrackTime])
  const startMinutesList = useMemo(() => Array(7).fill(null).map((_v, i) => minuteList("start", i)), [weeklyTrackTime])

  const endHoursList = useMemo(() => Array(7).fill(null).map((_v, i) => hourList("end", i)), [weeklyTrackTime])
  const endMinutesList = useMemo(() => Array(7).fill(null).map((_v, i) => minuteList("end", i)), [weeklyTrackTime])

  const changeWeeklyStartTrackTime = (hour: number, minute: number, weekNumber: number) => {
    const tmpSec = hour * 60 * 60 + minute * 60
    const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_stop_time || "24:00")
    if (tmpSec > stopTimeObj.hour * 60 * 60 + stopTimeObj.minute * 60) {
      const tmpWeeklyTime = weeklyTrackTime.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_stop_time,
          track_stop_time: v.track_stop_time,
        } : v
      ))
      setWeeklyTrackTime(tmpWeeklyTime)
    } else {
      const tmpWeeklyTime = weeklyTrackTime.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: secToTwoDigitsHourMinute(tmpSec),
          track_stop_time: v.track_stop_time,
        } : v
      ))
      setWeeklyTrackTime(tmpWeeklyTime)
    }
  }

  const changeWeeklyEndTrackTime = (hour: number, minute: number, weekNumber: number) => {
    const tmpSec = hour * 60 * 60 + minute * 60
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_start_time || "00:00")
    if (tmpSec < startTimeObj.hour * 60 * 60 + startTimeObj.minute * 60) {
      const tmpWeeklyTime = weeklyTrackTime.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: v.track_start_time,
        } : v
      ))
      setWeeklyTrackTime(tmpWeeklyTime)
    } else if (tmpSec >= 24 * 60 * 60 - 1) {
      const tmpWeeklyTime = weeklyTrackTime.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: "24:00",
        } : v
      ))
      setWeeklyTrackTime(tmpWeeklyTime)
    } else {
      const tmpWeeklyTime = weeklyTrackTime.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: secToTwoDigitsHourMinute(tmpSec),
        } : v
      ))
      setWeeklyTrackTime(tmpWeeklyTime)
    }
  }

  const closeModal = () => {
    setLimitTrackStartWeekNumbers([])
    setLimitTrackStopWeekNumbers([])
    setNoTrackWeekNumbers([])
    setSelectTypeList(["all", "all", "all", "all", "all", "all", "all"])
    closeCallback()
  }

  const saveWeeklyTrackTime = () => {
    const tmpWeeklyTimeData = weeklyTrackTime.map((v) => (
      noTrackWeekNumbers.includes(v.week_number) ? {
        week_number: v.week_number,
        track_start_time: "00:00",
        track_stop_time: "00:00",
      } : {
        week_number: v.week_number,
        track_start_time: v.track_start_time === "00:00" ? null : v.track_start_time,
        track_stop_time: v.track_stop_time === "24:00" ? null : v.track_stop_time,
      }
    ))
    if (editModalState.type === "updateOneself" || editModalState.type === "updateMember") {
      const params: patchMemberWeeklyTrackTimeRequest = {
        member_ids: targetMember.map((member) => member.id),
        weekly_track_times: tmpWeeklyTimeData,
      }
      dispatch(
        updateMembersWeeklyTrackTime(
          params,
          () => {
            alert.success("設定を変更しました")
            closeModal()
          },
        ),
      )
    } else if (editModalState.type === "createMember") {
      const params: postMemberWeeklyTrackTimeRequest = {
        member_ids: targetMember.map((member) => member.id),
        change_authority: false,
        weekly_track_times: tmpWeeklyTimeData,
      }
      dispatch(
        createMembersWeeklyTrackTime(
          params,
          () => {
            alert.success("個別設定を追加しました")
            closeModal()
          },
        ),
      )
    } else if (editModalState.type === "updateCompany") {
      const params: companyWeeklyTrackTime = {
        weekly_track_times: tmpWeeklyTimeData,
      }
      dispatch(
        updateCompanyWeeklyTrackTime(
          params,
          () => {
            alert.success("設定を変更しました")
            closeModal()
          },
        ),
      )
    }
  }

  const addTargetMember = (selectMemberId: number[]) => {
    setTargetMember(memberList.filter((v) => selectMemberId.includes(v.id)))
  }

  const removeTargetMember = (id: number) => {
    const tmp = targetMember.find((v) => v.id === id)
    if (tmp) {
      setTargetMember(targetMember.filter((v) => v.id !== id))
    }
  }

  const selectTrackType = (weekNum: number, type: string) => {
    if (type === "all") {
      setLimitTrackStartWeekNumbers(limitTrackStartWeekNumbers.filter((value) => value !== weekNum))
      setLimitTrackStopWeekNumbers(limitTrackStopWeekNumbers.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers(noTrackWeekNumbers.filter((value) => value !== weekNum))
      changeWeeklyStartTrackTime(0, 0, weekNum)
      changeWeeklyEndTrackTime(24, 0, weekNum)
    }
    if (type === "none") {
      setLimitTrackStartWeekNumbers(limitTrackStartWeekNumbers.filter((value) => value !== weekNum))
      setLimitTrackStopWeekNumbers(limitTrackStopWeekNumbers.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers(Array.from(new Set([...noTrackWeekNumbers, weekNum])))
      changeWeeklyStartTrackTime(0, 0, weekNum)
      changeWeeklyEndTrackTime(0, 0, weekNum)
    }
    if (type === "start") {
      setLimitTrackStartWeekNumbers(Array.from(new Set([...limitTrackStartWeekNumbers, weekNum])))
      setLimitTrackStopWeekNumbers(limitTrackStopWeekNumbers.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers(noTrackWeekNumbers.filter((value) => value !== weekNum))
      changeWeeklyStartTrackTime(0, 0, weekNum)
      changeWeeklyEndTrackTime(24, 0, weekNum)
    }
    if (type === "stop") {
      setLimitTrackStartWeekNumbers(limitTrackStartWeekNumbers.filter((value) => value !== weekNum))
      setLimitTrackStopWeekNumbers(Array.from(new Set([...limitTrackStopWeekNumbers, weekNum])))
      setNoTrackWeekNumbers(noTrackWeekNumbers.filter((value) => value !== weekNum))
      changeWeeklyStartTrackTime(0, 0, weekNum)
      changeWeeklyEndTrackTime(24, 0, weekNum)
    }
    if (type === "custom") {
      setLimitTrackStartWeekNumbers(Array.from(new Set([...limitTrackStartWeekNumbers, weekNum])))
      setLimitTrackStopWeekNumbers(Array.from(new Set([...limitTrackStopWeekNumbers, weekNum])))
      setNoTrackWeekNumbers(noTrackWeekNumbers.filter((value) => value !== weekNum))
      changeWeeklyStartTrackTime(0, 0, weekNum)
      changeWeeklyEndTrackTime(24, 0, weekNum)
    }
    setSelectTypeList(selectTypeList.map((v, i) => (i === weekNum ? type : v)))
  }

  return (
    <>
      {
        editModalState.display && (
          <div className="edit_modal_wrap">
            <div className={`edit_modal ${editModalState.display || 'none'}`}>
              <div className="edit_modal_header">
                { editModalState.type === "createMember" ? "新規追加" : "計測時間変更" }
                <button type="button" className="close_modal" onClick={closeModal}>
                  &times;
                </button>
              </div>
              <div className="edit_modal_body">
                {
                  editModalState.type === "updateCompany" && <div className="company_title">会社設定</div>
                }
                {
                  ["updateMember", "createMember"].includes(editModalState.type) && (
                    <fieldset className="target_members">
                      <legend
                        style={{
                          padding: "0px 8px",
                        }}
                      >
                        対象メンバー
                      </legend>
                      <div className="target_members_list">
                        {
                          targetMember?.map((v) => (
                            <div className={`track_member ${v.id}`} key={`track_member ${v.id}`}>
                              {
                                v.profile_image ? (
                                  <img
                                    alt="profile"
                                    src={v.profile_image}
                                    className="profile_icon"
                                  />
                                ) : (
                                  <img
                                    alt=""
                                    src={`/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                    className="profile_icon"
                                  />
                                )
                              }
                              {v.name}
                              {
                                editModalState.type === "createMember" && (
                                  <span style={{ cursor: "pointer" }} onClick={() => removeTargetMember(v.id)} role="none">
                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                  </span>
                                )
                              }
                            </div>
                          ))
                        }
                        {
                          editModalState.type === "createMember" && (
                            <AddMemberModule
                              memberIdList={targetMember.map((v) => v.id)}
                              callbackMembers={(val) => addTargetMember(val.map((w) => w.id))}
                              memberList={memberList.map((v) => ({ id: v.id, name: v.name || "", profile_image: v.profile_image, active: true }))}
                            />
                          )
                        }
                      </div>
                    </fieldset>
                  )
                }
                <fieldset className="setting_weekly_track">
                  <legend
                    style={{
                      padding: "0px 8px",
                    }}
                  >
                    計測時間設定
                  </legend>
                  <table className="setting_weekly_track_time_table">
                    <thead>
                      <tr>
                        <th>曜日</th>
                        <th>計測</th>
                        <th>計測開始時刻</th>
                        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                        <th />
                        <th>計測終了時刻</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        weeklyTrackTime && weeklyTrackTime.map((v) => (
                          <tr key={v.week_number}>
                            <td
                              style={{
                                color: weekDayColor(v.week_number),
                              }}
                            >
                              {weekDayDisplay(v.week_number)}
                            </td>
                            <td>
                              <select
                                style={{
                                  border: "solid 1px #999999",
                                  borderRadius: "4px",
                                  padding: "0.4em 0.8em",
                                  appearance: "auto",
                                  cursor: "pointer",
                                  backgroundColor: "#FFFFFF",
                                }}
                                onChange={(e) => selectTrackType(v.week_number, e.target.value)}
                                value={selectTypeList[v.week_number]}
                              >
                                <option value="all">
                                  全日計測する
                                </option>
                                <option value="none">
                                  計測しない
                                </option>
                                <option value="start">
                                  開始時間を設定する
                                </option>
                                <option value="stop">
                                  終了時間を設定する
                                </option>
                                <option value="custom">
                                  開始・終了時間を設定する
                                </option>
                              </select>
                            </td>
                            {
                              !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                <td>
                                  <div>
                                    {
                                      limitTrackStartWeekNumbers.includes(v.week_number) ? (
                                        <div>
                                          <div className="start_track_time">
                                            <select
                                              onChange={(e) => {
                                                changeWeeklyStartTrackTime(
                                                  Number(e.target.value),
                                                  digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").minute,
                                                  v.week_number,
                                                )
                                              }}
                                              value={digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").hour}
                                            >
                                              {startHoursList[v.week_number]}
                                            </select>
                                            :
                                            <select
                                              onChange={(e) => {
                                                changeWeeklyStartTrackTime(
                                                  digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").hour,
                                                  Number(e.target.value), v.week_number,
                                                )
                                              }}
                                              value={digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").minute}
                                            >
                                              {startMinutesList[v.week_number]}
                                            </select>
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ fontSize: "20px", color: "grey" }}>制限無し</div>
                                      )
                                    }
                                  </div>
                                </td>
                              )
                            }
                            <td
                              colSpan={["all", "none"].includes(selectTypeList[v.week_number]) ? 3 : 1}
                              style={{
                                width: ["all", "none"].includes(selectTypeList[v.week_number]) ? "55%" : "5%",
                              }}
                            >
                              {
                                selectTypeList[v.week_number] === "all" && (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      fontSize: "large",
                                      color: "#0f9655",
                                      fontWeight: "bold",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    終日計測する
                                  </div>
                                )
                              }
                              {
                                selectTypeList[v.week_number] === "none" && (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      fontSize: "large",
                                      color: "red",
                                      fontWeight: "bold",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    計測しない
                                  </div>
                                )
                              }
                              {
                                !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                  <div
                                    style={{
                                      fontSize: "18px",
                                    }}
                                  >
                                    ~
                                  </div>
                                )
                              }
                            </td>
                            {
                              !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                <td>
                                  <div>
                                    {
                                      limitTrackStopWeekNumbers.includes(v.week_number) ? (
                                        <div>
                                          <div className="end_track_time">
                                            <select
                                              onChange={(e) => {
                                                changeWeeklyEndTrackTime(
                                                  Number(e.target.value),
                                                  digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").minute,
                                                  v.week_number,
                                                )
                                              }}
                                              value={digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").hour}
                                            >
                                              {endHoursList[v.week_number]}
                                            </select>
                                            :
                                            <select
                                              onChange={(e) => {
                                                changeWeeklyEndTrackTime(
                                                  digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").hour,
                                                  Number(e.target.value),
                                                  v.week_number,
                                                )
                                              }}
                                              value={digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").minute}
                                            >
                                              {endMinutesList[v.week_number]}
                                            </select>
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ fontSize: "20px", color: "grey" }}>制限無し</div>
                                      )
                                    }
                                  </div>
                                </td>
                              )
                            }
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </fieldset>
              </div>
              <div className="edit_modal_footer">
                <button
                  type="button"
                  onClick={closeModal}
                  className="button cancel"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={saveWeeklyTrackTime}
                  className="button"
                >
                  { editModalState.type === "createMember" ? "追加" : "変更" }
                </button>
              </div>
            </div>
            <div
              className="gray_area"
              onClick={closeModal}
              role="none"
            />
          </div>
        )
      }
    </>
  )
}

export default EditModal
